<template>
  <div class="login-page">
    <div class="login-card">
      <div class="brand">
        <div class="brand-icon">
          <el-icon :size="26"><Notebook /></el-icon>
        </div>
        <h1>生活小妙招</h1>
        <p>欢迎回来，登录后继续你的文档之旅</p>
      </div>

      <div class="mode-switch">
        <button
          type="button"
          :class="{ active: isLogin }"
          @click="switchToLogin"
        >登录</button>
        <button
          type="button"
          :class="{ active: isRegister }"
          @click="toggleRegister"
        >注册</button>
        <button
          type="button"
          :class="{ active: isResetPassword }"
          @click="toggleResetPassword"
        >重置密码</button>
      </div>

      <!-- 登录 -->
      <el-form v-if="isLogin" class="auth-form" @submit.prevent="handleLogin">
        <el-radio-group
          v-model="loginMethod"
          class="login-method"
          @change="handleLoginMethodChange"
        >
          <el-radio-button value="password">密码登录</el-radio-button>
          <el-radio-button value="captcha">验证码登录</el-radio-button>
        </el-radio-group>

        <el-form-item v-if="loginMethod === 'password'">
          <el-input
            v-model="username"
            placeholder="用户名 / 手机号"
            size="large"
            clearable
          >
            <template #prefix>
              <el-icon><User /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item v-if="loginMethod === 'password'">
          <el-input
            v-model="password"
            type="password"
            placeholder="密码"
            size="large"
            show-password
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <template v-if="loginMethod === 'captcha'">
          <el-form-item>
            <el-input
              v-model="phone"
              placeholder="手机号"
              size="large"
              clearable
            >
              <template #prefix>
                <el-icon><Iphone /></el-icon>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item>
            <div class="captcha-row">
              <canvas
                ref="captchaCanvas"
                width="100"
                height="40"
                class="captcha-canvas"
                @click="refreshCaptcha"
              ></canvas>
              <el-input v-model="imageCode" placeholder="图片验证码" size="large" />
            </div>
          </el-form-item>

          <el-form-item>
            <div class="sms-row">
              <el-input
                v-model="smsCode"
                placeholder="短信验证码"
                size="large"
              >
                <template #prefix>
                  <el-icon><Message /></el-icon>
                </template>
              </el-input>
              <el-button :disabled="isCooldown" @click="sendSmsCode">
                {{ buttonText }}
              </el-button>
            </div>
          </el-form-item>
        </template>

        <el-button
          type="primary"
          size="large"
          native-type="submit"
          class="submit-btn"
        >
          登 录
        </el-button>

        <div class="form-links">
          <el-link type="primary" :underline="false" @click="toggleRegister">
            还没有账号？立即注册
          </el-link>
          <el-link type="info" :underline="false" @click="toggleResetPassword">
            忘记密码？
          </el-link>
        </div>
      </el-form>

      <!-- 注册 -->
      <el-form v-if="isRegister" class="auth-form" @submit.prevent="handleRegister">
        <el-form-item>
          <el-input
            v-model="registerUsername"
            placeholder="用户名"
            size="large"
            clearable
          >
            <template #prefix>
              <el-icon><User /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <el-input
            v-model="registerPhone"
            placeholder="手机号"
            size="large"
            clearable
          >
            <template #prefix>
              <el-icon><Iphone /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <el-input
            v-model="registerEmail"
            placeholder="邮箱"
            size="large"
            clearable
          >
            <template #prefix>
              <el-icon><Message /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <div class="two-col">
          <el-form-item>
            <el-input v-model="registerChineseName" placeholder="中文名（选填）" size="large" />
          </el-form-item>
          <el-form-item>
            <el-input v-model="registerFullName" placeholder="全名（选填）" size="large" />
          </el-form-item>
        </div>

        <el-form-item>
          <el-input
            v-model="registerPassword"
            type="password"
            placeholder="密码（至少包含字母大小写、数字和特殊符号中的两种）"
            size="large"
            show-password
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <el-input
            v-model="confirmPassword"
            type="password"
            placeholder="确认密码"
            size="large"
            show-password
            @blur="checkPasswordMatch"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <p v-if="passwordMismatch" class="field-error">两次输入的密码不一致，请重新输入。</p>

        <el-form-item>
          <div class="captcha-row">
            <canvas
              ref="registerCaptchaCanvas"
              width="100"
              height="40"
              class="captcha-canvas"
              @click="refreshRegisterCaptcha"
            ></canvas>
            <el-input v-model="registerImageCode" placeholder="图片验证码" size="large" />
          </div>
        </el-form-item>

        <el-form-item>
          <div class="sms-row">
            <el-input
              v-model="registerSmsCode"
              placeholder="短信验证码"
              size="large"
            >
              <template #prefix>
                <el-icon><Message /></el-icon>
              </template>
            </el-input>
            <el-button :disabled="isRegisterCooldown" @click="sendRegisterSmsCode">
              {{ buttonRegisterText }}
            </el-button>
          </div>
        </el-form-item>

        <el-button
          type="primary"
          size="large"
          native-type="submit"
          class="submit-btn"
        >
          注 册
        </el-button>

        <div class="form-links center">
          <el-link type="primary" :underline="false" @click="toggleRegister">
            已有账号？返回登录
          </el-link>
        </div>
      </el-form>

      <!-- 重置密码 -->
      <el-form v-if="isResetPassword" class="auth-form" @submit.prevent="handleResetPassword">
        <el-form-item>
          <el-input
            v-model="resetUsername"
            placeholder="用户名 / 手机号"
            size="large"
            clearable
          >
            <template #prefix>
              <el-icon><User /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <el-input
            v-model="resetPassword"
            type="password"
            placeholder="新密码"
            size="large"
            show-password
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <el-input
            v-model="confirmResetPassword"
            type="password"
            placeholder="确认新密码"
            size="large"
            show-password
            @blur="checkResetPasswordMatch"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <p v-if="resetPasswordMismatch" class="field-error">两次输入的密码不一致，请重新输入。</p>

        <el-form-item>
          <div class="captcha-row">
            <canvas
              ref="resetCaptchaCanvas"
              width="100"
              height="40"
              class="captcha-canvas"
              @click="refreshResetCaptcha"
            ></canvas>
            <el-input v-model="resetImageCode" placeholder="图片验证码" size="large" />
          </div>
        </el-form-item>

        <el-form-item>
          <div class="sms-row">
            <el-input
              v-model="resetSmsCode"
              placeholder="短信验证码"
              size="large"
            >
              <template #prefix>
                <el-icon><Message /></el-icon>
              </template>
            </el-input>
            <el-button :disabled="isResetCooldown" @click="sendResetSmsCode">
              {{ buttonResetText }}
            </el-button>
          </div>
        </el-form-item>

        <el-button
          type="primary"
          size="large"
          native-type="submit"
          class="submit-btn"
        >
          重置密码
        </el-button>

        <div class="form-links center">
          <el-link type="primary" :underline="false" @click="toggleResetPassword">
            返回登录
          </el-link>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
import Cookies from 'js-cookie';
import axios from "axios";
import JSEncrypt from 'jsencrypt';

export default {
  data() {
    return {
      userLoginArg: {
        username: '',
        password: '',
        code: '',
      },
      userRegisterArg: {
        username: '',
        phone: '',
        password: '',
        email: '',
        chineseName: '',
        fullName: '',
        code: '',
      },
      sendSmsCodeArg: {
        requestId: '',
        captcha: '',
        phone: '',
      },
      verifySmsCodeArg: {
        phone: '',
        code: '',
      },
      username: '',
      password: '',
      phone: '',
      imageCode: '',
      smsCode: '',
      isCooldown: false,
      cooldownTime: 60,
      timer: null,
      buttonText: '获取验证码',
      isLogin: true,
      registerPhone: '',
      registerSmsCode: '',
      registerImageCode: '',
      registerUsername: '',
      registerEmail: '',
      registerChineseName: '',
      registerFullName: '',
      registerPassword: '',
      confirmPassword: '',
      isRegister: false,
      isRegisterCooldown: false,
      cooldownTimeRegister: 60,
      timerRegister: null,
      buttonRegisterText: '获取验证码',
      captchaValue: '',
      registerCaptchaValue: '',
      requestId: '',
      passwordMismatch: false,
      loginMethod: 'password',
      resetUsername: '',
      resetImageCode: '',
      resetSmsCode: '',
      resetPassword: '',
      confirmResetPassword: '',
      resetPasswordMismatch: false,
      isResetPassword: false,
      isResetCooldown: false,
      cooldownTimeReset: 60,
      timerReset: null,
      buttonResetText: '获取验证码',
      publicKey: 'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEArOylIc3UY6oQ5NbFDGJqI9MjCIxTbtt4KsMn22zAZKyF4UpK7Qxbrdwamtp9ia1543YcTlMl5qPE04leeNSCezpi7AfIxEC+LKluuEqZgr9Qn3DpptCbzjNFSLXnySqsu3N7yPVv2kcDi2bAV8MdV4pY4lNHi1MyPT3vmdWsrS5cgWHYwVvVZej+AlkraV44SWrcwHZPSCOT1pnGJ15/EHU91CVWp9z+AJLeAFhTCweZbvgg9oRF0XGqtlRBiePCqKTlpRhjsiUc0V/XsoDwYVM+HCx+erkE+pms6utCaOzvOATucUbQnkuprqudQwZedAd5OmVOPvuj3Akr+dAaJQIDAQAB',
    };
  },
  methods: {
    handleLoginMethodChange(val) {
      if (val === 'password') {
        this.resetFields();
      } else {
        this.refreshCaptcha();
      }
    },
    switchToLogin() {
      this.isLogin = true;
      this.isRegister = false;
      this.isResetPassword = false;
      this.resetFields();
    },
    resetFields() {
      this.username = '';
      this.password = '';
      this.phone = '';
      this.imageCode = '';
      this.smsCode = '';
    },
    generateUniqueRequestId() {
      this.requestId = 'req_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
      return this.requestId;
    },
    validatePhone(phone) {
      const phoneRegex = /^1[3-9]\d{9}$/;
      return phoneRegex.test(phone);
    },
    checkPasswordMatch() {
      this.passwordMismatch = this.registerPassword !== this.confirmPassword;
    },
    checkResetPasswordMatch() {
      this.resetPasswordMismatch = this.resetPassword !== this.confirmResetPassword;
    },
    async refreshCaptcha() {
      if (this.loginMethod === 'password') {
        return;
      }
      const requestId = this.generateUniqueRequestId();
      const response = await axios.get(`/api/captcha/getImageCaptcha?requestId=${requestId}`);
      this.captchaValue = response.data.data;
      this.drawCaptcha();
    },
    async refreshRegisterCaptcha() {
      const requestId = this.generateUniqueRequestId();
      const response = await axios.get(`/api/captcha/getImageCaptcha?requestId=${requestId}`);
      this.registerCaptchaValue = response.data.data;
      this.drawRegisterCaptcha();
    },
    drawCaptcha() {
      const canvas = this.$refs.captchaCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#f0f4ff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#c7d2fe';
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.stroke();
      }
      ctx.font = '20px Arial';
      ctx.fillStyle = '#4f6ef7';
      ctx.fillText(this.captchaValue, 10, 27);
    },
    drawRegisterCaptcha() {
      const canvas = this.$refs.registerCaptchaCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#f0f4ff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#c7d2fe';
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.stroke();
      }
      ctx.font = '20px Arial';
      ctx.fillStyle = '#4f6ef7';
      ctx.fillText(this.registerCaptchaValue, 10, 27);
    },
    drawResetCaptcha() {
      const canvas = this.$refs.resetCaptchaCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#f0f4ff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#c7d2fe';
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.stroke();
      }
      ctx.font = '20px Arial';
      ctx.fillStyle = '#4f6ef7';
      ctx.fillText(this.resetCaptchaValue, 10, 27);
    },
    async sendSmsCode() {
      if (!this.validatePhone(this.phone)) {
        this.$message.warning('请输入有效的手机号');
        return;
      }
      if (!this.imageCode) {
        this.$message.warning('请输入图片验证码');
        return;
      }
      this.sendSmsCodeArg = {
        requestId: this.requestId,
        captcha: this.imageCode,
        phone: this.phone,
        scene: 'LOGIN',
      };
      const response = await axios.post(`/api/captcha/sendSmsCode`, this.sendSmsCodeArg);
      if (response.data.code !== 0) {
        this.$message.error(response.data.message);
        if (response.data.code === 4004) {
          this.refreshCaptcha();
        }
      } else {
        this.startCooldown();
      }
    },
    startCooldown() {
      this.isCooldown = true;
      this.buttonText = `${this.cooldownTime}秒后重发`;

      this.timer = setInterval(() => {
        this.cooldownTime--;
        if (this.cooldownTime <= 0) {
          clearInterval(this.timer);
          this.isCooldown = false;
          this.cooldownTime = 60;
          this.buttonText = '获取验证码';
        } else {
          this.buttonText = `${this.cooldownTime}秒后重发`;
        }
      }, 1000);
    },
    startRegisterCooldown() {
      this.isRegisterCooldown = true;
      this.buttonRegisterText = `${this.cooldownTimeRegister}秒后重发`;

      this.timerRegister = setInterval(() => {
        this.cooldownTimeRegister--;
        if (this.cooldownTimeRegister <= 0) {
          clearInterval(this.timerRegister);
          this.isRegisterCooldown = false;
          this.cooldownTimeRegister = 60;
          this.buttonRegisterText = '获取验证码';
        } else {
          this.buttonRegisterText = `${this.cooldownTimeRegister}秒后重发`;
        }
      }, 1000);
    },
    startResetCooldown() {
      this.isResetCooldown = true;
      this.buttonResetText = `${this.cooldownTimeReset}秒后重发`;

      this.timerReset = setInterval(() => {
        this.cooldownTimeReset--;
        if (this.cooldownTimeReset <= 0) {
          clearInterval(this.timerReset);
          this.isResetCooldown = false;
          this.cooldownTimeReset = 60;
          this.buttonResetText = '获取验证码';
        } else {
          this.buttonResetText = `${this.cooldownTimeReset}秒后重发`;
        }
      }, 1000);
    },
    async sendRegisterSmsCode() {
      if (!this.validatePhone(this.registerPhone)) {
        this.$message.warning('请输入有效的手机号');
        return;
      }
      if (!this.registerImageCode) {
        this.$message.warning('请输入图片验证码');
        return;
      }
      this.sendSmsCodeArg = {
        requestId: this.requestId,
        captcha: this.registerImageCode,
        phone: this.registerPhone,
        scene: 'REGISTER',
      };
      const response = await axios.post(`/api/captcha/sendSmsCode`, this.sendSmsCodeArg);
      if (response.data.code !== 0) {
        this.$message.error(response.data.message);
        if (response.data.code === 4004) {
          this.refreshRegisterCaptcha();
        }
      } else {
        this.startRegisterCooldown();
      }
    },
    async handleLogin() {
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(this.publicKey);
      if (this.loginMethod === 'password') {
        if (!this.username || !this.password) {
          this.$message.warning('请输入用户名和密码');
          return;
        }
        this.userLoginArg = {
          username: this.username,
          password: encrypt.encrypt(this.password),
        };
      } else {
        if (!this.validatePhone(this.phone)) {
          this.$message.warning('请输入有效的手机号');
          return;
        }
        this.userLoginArg = {
          username: this.phone,
          code: this.smsCode,
        };
      }
      const response = await axios.post(`/api/user/login`, this.userLoginArg);
      if (response.data.code !== 0) {
        this.$message.error(response.data.message);
      } else {
        await this.processToken(response);
      }
    },
    async processToken(response) {
      Cookies.set('token', response.data.data, { expires: 7 });
      const redirectUrl = sessionStorage.getItem('redirectUrl');
      if (redirectUrl) {
        sessionStorage.removeItem('redirectUrl');
        this.$router.push(redirectUrl);
      } else {
        this.$router.push('/document');
      }
    },
    async handleRegister() {
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(this.publicKey);
      this.userRegisterArg = {
        username: this.registerUsername,
        phone: this.registerPhone,
        code: this.registerSmsCode,
        password: encrypt.encrypt(this.registerPassword),
        email: this.registerEmail,
        chineseName: this.registerChineseName,
        fullName: this.registerFullName,
      };
      const response = await axios.post(`/api/user/register`, this.userRegisterArg);
      if (response.data.code !== 0) {
        this.$message.error(response.data.message);
      } else {
        this.toggleRegister();
        await this.processToken(response);
      }
    },
    toggleRegister() {
      this.isRegister = !this.isRegister;
      if (this.isRegister) {
        this.isResetPassword = false;
        this.isLogin = false;
      } else {
        this.isLogin = true;
      }
      this.username = '';
      this.password = '';
      this.phone = '';
      this.imageCode = '';
      this.smsCode = '';
      if (this.isRegister) {
        this.refreshRegisterCaptcha();
      } else {
        this.refreshCaptcha();
      }
    },
    toggleResetPassword() {
      this.isResetPassword = !this.isResetPassword;
      if (this.isResetPassword) {
        this.isRegister = false;
        this.isLogin = false;
      } else {
        this.isLogin = true;
      }
      this.username = '';
      this.password = '';
      this.phone = '';
      this.imageCode = '';
      this.smsCode = '';
      this.resetUsername = '';
      this.resetImageCode = '';
      this.resetSmsCode = '';
      this.resetPassword = '';
      this.confirmResetPassword = '';
      this.resetPasswordMismatch = false;

      if (this.isResetPassword) {
        this.refreshResetCaptcha();
      } else {
        this.refreshCaptcha();
      }
    },
    async refreshResetCaptcha() {
      const requestId = this.generateUniqueRequestId();
      const response = await axios.get(`/api/captcha/getImageCaptcha?requestId=${requestId}`);
      this.resetCaptchaValue = response.data.data;
      this.drawResetCaptcha();
    },
    async sendResetSmsCode() {
      if (!this.validatePhone(this.resetUsername)) {
        this.$message.warning('请输入有效的手机号');
        return;
      }
      if (!this.resetImageCode) {
        this.$message.warning('请输入图片验证码');
        return;
      }
      this.sendSmsCodeArg = {
        requestId: this.requestId,
        captcha: this.resetImageCode,
        phone: this.resetUsername,
        scene: 'RESET_PASSWORD',
      };
      const response = await axios.post(`/api/captcha/sendSmsCode`, this.sendSmsCodeArg);
      if (response.data.code !== 0) {
        this.$message.error(response.data.message);
        if (response.data.code === 4004) {
          this.refreshResetCaptcha();
        }
      } else {
        this.startResetCooldown();
      }
    },
    async handleResetPassword() {
      if (this.resetPassword !== this.confirmResetPassword) {
        this.resetPasswordMismatch = true;
        return;
      }
      this.resetPasswordMismatch = false;

      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(this.publicKey);
      const resetPayload = {
        username: this.resetUsername,
        code: this.resetSmsCode,
        newPassword: encrypt.encrypt(this.resetPassword),
      };
      const response = await axios.post(`/api/user/resetPassword`, resetPayload);
      if (response.data.code !== 0) {
        this.$message.error(response.data.message);
      } else {
        this.toggleResetPassword();
        await this.processToken(response);
      }
    },
  },
  mounted() {
    this.refreshCaptcha();
  },
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  box-sizing: border-box;
  background: linear-gradient(160deg, #eef2ff 0%, #f5f7fb 45%, #f0f4ff 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC",
    "Hiragino Sans GB", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif;
  --el-color-primary: #4f6ef7;
  --el-color-primary-light-3: #7b93fa;
  --el-color-primary-light-5: #a5b6fb;
  --el-color-primary-light-7: #cdd8fd;
  --el-color-primary-light-8: #e2e9fe;
  --el-color-primary-light-9: #f0f4ff;
  --el-color-primary-dark-2: #3f58c6;
}

.login-card {
  width: 100%;
  max-width: 460px;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 10px 40px rgba(16, 24, 40, 0.1);
  padding: 36px 40px 40px;
  box-sizing: border-box;
}

.brand {
  text-align: center;
  margin-bottom: 24px;
}

.brand-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}

.brand h1 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 600;
  color: #1f2329;
}

.brand p {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}

.mode-switch {
  display: flex;
  background: #f2f4fa;
  border-radius: 10px;
  padding: 4px;
  margin-bottom: 24px;
}

.mode-switch button {
  flex: 1;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.mode-switch button.active {
  background: #ffffff;
  color: var(--el-color-primary);
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.1);
}

.auth-form .el-form-item {
  margin-bottom: 18px;
}

.login-method {
  display: flex;
  width: 100%;
  margin-bottom: 20px;
}

.login-method :deep(.el-radio-button) {
  flex: 1;
}

.login-method :deep(.el-radio-button__inner) {
  width: 100%;
  border-color: #dcdfe6;
}

.captcha-row,
.sms-row {
  display: flex;
  gap: 10px;
  width: 100%;
}

.captcha-row .el-input,
.sms-row .el-input {
  flex: 1;
}

.captcha-canvas {
  flex-shrink: 0;
  width: 100px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  cursor: pointer;
  background: #f5f7fa;
}

.sms-row .el-button {
  width: 128px;
  margin: 0;
  flex-shrink: 0;
}

.two-col {
  display: flex;
  gap: 10px;
}

.two-col .el-form-item {
  flex: 1;
}

.submit-btn {
  width: 100%;
  height: 44px;
  margin-top: 4px;
  font-size: 15px;
  border-radius: 8px;
}

.form-links {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
}

.form-links.center {
  justify-content: center;
}

.field-error {
  margin: -8px 0 12px;
  font-size: 12px;
  color: #f56c6c;
}
</style>
